import React from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, ShoppingBag, ClipboardList, Image, User, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const LINKS = [
  { to: "/gym", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/gym/catalog", label: "Wholesale Catalog", icon: ShoppingBag },
  { to: "/gym/orders", label: "Orders & Reorder", icon: ClipboardList },
  { to: "/gym/artwork", label: "Academy Artwork", icon: Image },
  { to: "/gym/account", label: "Account", icon: User },
];

export function GymLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0A0A0C] flex flex-col lg:flex-row">
      <aside className="lg:w-64 lg:min-h-screen border-b lg:border-b-0 lg:border-r border-white/10 bg-black">
        <Link to="/" className="flex items-center gap-2.5 p-5 border-b border-white/10">
          <img src="/paragon-logo.jpeg" alt="Paragon" className="h-10 w-10 rounded-full" />
          <div>
            <div className="font-heading font-black uppercase text-white leading-none">Wholesale</div>
            <div className="font-mono text-[9px] tracking-widest text-emerald-400 uppercase">Gym Portal</div>
          </div>
        </Link>
        <nav className="flex lg:flex-col p-3 gap-1 overflow-x-auto">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`gym-nav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-md font-heading font-semibold uppercase text-sm tracking-wide whitespace-nowrap transition-colors ${
                  isActive ? "bg-emerald-500/15 text-emerald-400" : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <l.icon className="h-4 w-4" /> {l.label}
            </NavLink>
          ))}
          <button
            data-testid="gym-logout"
            onClick={async () => { await logout(); navigate("/"); }}
            className="flex items-center gap-3 px-4 py-3 rounded-md font-heading font-semibold uppercase text-sm tracking-wide text-zinc-500 hover:text-red-400"
          >
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </nav>
      </aside>
      <main className="flex-1 min-w-0">
        <div className="px-5 lg:px-8 py-4 border-b border-white/10 bg-black/40">
          <p className="font-mono text-xs text-zinc-500">Signed in as {user?.name} · {user?.email}</p>
        </div>
        <Outlet />
      </main>
    </div>
  );
}

export function MatrixStepper({ sizes, sizePrices, quantities, onChange }) {
  return (
    <div className="divide-y divide-white/5 border border-white/10 rounded-lg overflow-hidden">
      {sizes.map((s) => (
        <div key={s} className="flex items-center justify-between px-4 py-3 bg-[#121216]">
          <div className="flex items-center gap-3">
            <span className="font-mono font-bold text-white w-12">{s}</span>
            <span className="font-mono text-xs text-zinc-500">{sizePrices?.[s] != null ? `$${Number(sizePrices[s]).toFixed(2)} ea` : ""}</span>
          </div>
          <div className="flex items-center border border-white/15 rounded-sm">
            <button
              type="button"
              data-testid={`matrix-dec-${s}`}
              onClick={() => onChange(s, Math.max(0, (quantities[s] || 0) - 1))}
              className="px-3 py-2 text-zinc-300 hover:text-emerald-400 font-bold"
            >−</button>
            <input
              data-testid={`matrix-qty-${s}`}
              type="number"
              value={quantities[s] || 0}
              onChange={(e) => onChange(s, Math.max(0, parseInt(e.target.value || "0", 10)))}
              className="w-14 text-center bg-transparent font-mono text-white outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <button
              type="button"
              data-testid={`matrix-inc-${s}`}
              onClick={() => onChange(s, (quantities[s] || 0) + 1)}
              className="px-3 py-2 text-zinc-300 hover:text-emerald-400 font-bold"
            >+</button>
          </div>
        </div>
      ))}
    </div>
  );
}
